/* eslint-disable react/jsx-props-no-spreading */
import React, {
  createRef, useEffect, useRef, useState,
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import {
  CircularProgress,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import _ from 'lodash';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { RootState } from '@/store/rootReducer';
import Spacer from '@/components/ui-components/Spacer';
import useScrollPosition from '@/hooks/useScrollPosition';
import {
  ControlledCompanyInformation,
  ControlledTeam,
  ControlledOfficialFilings,
  ControlledPerks,
  ControlledCommitments,
  ControlledFinancials,
  ControlledTerms,
  ControlledTimeline,
} from './v2/Controllers';
import SummaryController from './v2/Summary';
import Typography from '../components/Typography';

const useStyles = makeStyles((theme) => ({
  gridPadding: {
    padding: theme.spacing(1),
  },
  loadingDiv: {
    margin: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingSpinner: {
    marginBottom: theme.spacing(1),
  },
  header: {
    // backgroundImage: "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.557292) 44.27%, rgba(255, 255, 255, 0.5625) 59.9%, rgba(255, 255, 255, 0) 100%), url('https://images.pexels.com/photos/374023/pexels-photo-374023.jpeg');",
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  headerName: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(1),
  },
  description: {
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  live: {
    backgroundColor: theme.palette.positive.main,
    color: 'white',
  },
  close: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
  },
  favoriteIconToggled: {
    color: theme.palette.error.main,
  },
  overflow: {
    // overflowY: 'auto',
    height: '100%',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '& > *': {
      marginBottom: theme.spacing(1),
    },
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  chips: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  tocContainer: {
    position: 'sticky',
    top: 80,
  },
  tocContainerMobile: {
    position: 'sticky',
    top: 56,
    overflowX: 'hidden',
    msOverflowStyle: 'none' /* IE and Edge */,
    scrollbarWidth: 'none' /* Firefox */,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    zIndex: 9999,
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    // overflowX: 'hidden',
    // boxShadow: theme.shadows[10],
    boxShadow: '0 6px 3px -3px gray',
  },
  tocContainerMobileFlexbx: {
    // position: 'fixed',
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    // marginLeft: -theme.spacing(2),
    // marginRight: -theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    width: 900,
    zIndex: 9999,
  },
  tocBtn: {
    marginTop: theme.spacing(1),
    justifyContent: 'flex-start',
    textTransform: 'none',
    minHeight: '42px',
  },
  tocBtnInView: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.extralight,
  },
  tocBtnHorizontal: {
    flexGrow: 1,
    // marginRight: MARGIN_BETWEEN_BUTTONS,
  },
}));

interface Props {
  withMarginTop: boolean;
}

const defaultProps = {};

function shouldRender(id: string, offering: RootState['markets']['offering']) {
  switch (id) {
    case 'companyInformation':
      return !_.isEmpty(offering.data?.company_information);
    case 'team':
      return !_.isEmpty(offering.data?.team);
    case 'perks':
      return (offering.data?.perks && !_.isEmpty(offering.data?.perks));
    case 'officialFilings':
      return !_.isEmpty(offering.data?.official_accesions);
    case 'financials':
      return !_.isEmpty(offering.data?.financials);
    case 'terms':
      return offering.data?.terms && offering.data?.terms.standard.name !== 'UNKNOWN' && offering.data?.terms.standard.name !== 'OTHER' && !_.isEmpty(offering.data?.terms);
    case 'timeline':
      return !_.isEmpty(offering.data?.timeline);
    case 'commitments':
      return (
        offering.data?.commitments && offering.data?.commitments.chartLength
        && !_.isEmpty(offering.data?.commitments)
      );
      /* return (!_.isEmpty(offering.data?.commitments)); */
    default:
      return false;
  }
}

const ITEMS = [
  {
    id: 'companyInformation',
    component: <ControlledCompanyInformation />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'commitments',
    component: <ControlledCommitments />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'financials',
    component: <ControlledFinancials />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'officialFilings',
    component: <ControlledOfficialFilings />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'perks',
    component: <ControlledPerks />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'team',
    component: <ControlledTeam />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'terms',
    component: <ControlledTerms />,
    y: 0,
    bottom: 0,
  },
  {
    id: 'timeline',
    component: <ControlledTimeline />,
    y: 0,
    bottom: 0,
  },
];

interface TOCProps {
  items: {
    id: string;
    component: React.ReactElement;
    y: number;
    bottom: number;
  }[];
  scrollTo: () => void;
  horizontalMode?: boolean;
  // contRef: HTMLImageElement;
}

const MARGIN_BETWEEN_BUTTONS = 30;

const TOC = ({
  items, scrollTo, elRefs, horizontalMode = false, contRef,
}: TOCProps) => {
  const [tocItemInView, setTocItemInView] = useState(0);
  const scrollY = useScrollPosition();
  const classes = useStyles();
  const { t } = useTranslation('markets');

  useEffect(() => {
    const availableItems = items.filter((item) => item.bottom < scrollY);
    if (availableItems) {
      setTocItemInView(
        (items.findIndex((x) => x === availableItems[availableItems.length - 1]) + 1),
      );
    } else {
      setTocItemInView(0);
    }
  }, [scrollY]);

  const [offset, setOffset] = useState(0);
  const [buttonRefs, setButtonRefs] = useState([]);
  useEffect(() => {
    // add or remove refs
    setButtonRefs((prevElRefs) => Array(elRefs.length)
      .fill()
      .map((_, i) => prevElRefs[i] || createRef()));
  }, [elRefs]);

  useEffect(() => {
    const offsetLeft = buttonRefs.reduce((acc, el, index) => {
      if (index >= tocItemInView) return acc;
      return (acc + el.current.offsetWidth + MARGIN_BETWEEN_BUTTONS);
    }, 0);
    setOffset((-1) * offsetLeft);
  }, [tocItemInView, buttonRefs]);

  return (
    <>
      {items.map((item, i) => (
        <motion.div
          key={item.id}
          animate={{
            x: horizontalMode ? offset : 0,
          }}
          style={{
            display: 'flex',
          }}
          // drag="x"
          // onDrag={(event, info) => {
          //   setOffset((prev) => prev + info.point.x);
          // }}
        >
          <Button
            onClick={() => scrollTo(elRefs[i].current)}
            fullWidth={!horizontalMode}
            ref={buttonRefs[i]}
            className={clsx(
              classes.tocBtn,
              horizontalMode && classes.tocBtnHorizontal,
              tocItemInView === i && classes.tocBtnInView,
            )}
          >
            <Typography variant="baseBold" color="primary">
              {t(`markets.detailView.${item.id}.title` as const)}
            </Typography>
          </Button>
          <span style={{ minWidth: MARGIN_BETWEEN_BUTTONS }} />
        </motion.div>
      ))}
    </>
  );
};

const DetailView = ({ withMarginTop }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation(['markets', 'components']);
  const { offering } = useSelector((state: RootState) => state.markets);
  const [elRefs, setElRefs] = React.useState([]);
  const contRef = useRef<HTMLImageElement | null>(null);
  const [items, setItems] = useState(
    ITEMS.filter((item) => shouldRender(item.id, offering)).map((item, i) => ({
      ...item,
      y: elRefs[i]?.current?.getBoundingClientRect().top || 0,
      bottom: elRefs[i]?.current?.getBoundingClientRect().bottom || 0,
    })),
  );
  useEffect(() => {
    // add or remove refs
    setElRefs((prevElRefs) => Array(ITEMS.length)
      .fill()
      .map((_, i) => prevElRefs[i] || createRef()));
  }, []);
  useEffect(() => {
    setItems(
      ITEMS.filter((item) => shouldRender(item.id, offering)).map(
        (item, i) => ({
          ...item,
          y: elRefs[i]?.current?.getBoundingClientRect().top || 0,
          bottom: (elRefs[i]?.current?.getBoundingClientRect().bottom - 69) || 0,
        }),
      ),
    );
  }, [offering, elRefs]);

  const scrollTo = (element) => {
    const y = element.getBoundingClientRect().top + window.pageYOffset - 70;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {offering.isLoading && (
        <div className={classes.overflow}>
          <div className={classes.loadingDiv}>
            <CircularProgress className={classes.loadingSpinner} />
            <Typography color="textSecondary" variant="body2">
              {t('components:loadingScreen.loading')}
            </Typography>
          </div>
        </div>
      )}

      {!offering.isLoading && (
        <div className={classes.overflow}>
          {/*  <OfferingSummary withMarginTop={withMarginTop} /> */}
          {/* <SummaryController
            withMarginTop={withMarginTop}
            offering={offering.data!}
          /> */}
          <Spacer spacing={3} />
          <Grid
            container
            spacing={1}
            direction="row"
            justify="center"
            alignItems="flex-start"
            alignContent="center"
          >
            {/* Scrol menu */}
            {isMobile ? (
              <div className={classes.tocContainerMobile} ref={contRef}>
                <motion.div
                  className={classes.tocContainerMobileFlexbx}
                  // drag="x"
                  // dragConstraints={{
                  //   left: 0,
                  //   right: 400,
                  // }}
                >
                  <TOC
                    items={items}
                    scrollTo={scrollTo}
                    elRefs={elRefs}
                    contRef={contRef}
                    horizontalMode
                  />
                </motion.div>
              </div>
            ) : (
              <Grid item xs={3} className={classes.tocContainer} ref={contRef}>
                <TOC items={items} scrollTo={scrollTo} elRefs={elRefs} contRef={contRef} />
              </Grid>
            )}
            {/* Offer content */}
            <Grid item xs={12} md={9}>
              {isMobile && <Spacer spacing={2} />}
              {items.map((item, i) => (
                <>
                  <div
                    key={item.id}
                    ref={elRefs[i]}
                    style={{
                      borderBottom: `1px solid ${theme.palette.greyScale.light}`,
                    }}
                  >
                    {item.component}
                    <Spacer spacing={6} />
                  </div>
                  <Spacer spacing={2} />
                </>
              ))}
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
DetailView.defaultProps = (defaultProps as unknown) as Partial<Props>;
export default DetailView;
